import { clockMinutes } from './todayClock';
import { snowflakeId } from '../journey-planning/planningModel';
import { lifecycleActionCapabilities } from '../journey-workspace/journeyLifecycleModel';

export const TODAY_LIVE_LIFECYCLES = Object.freeze(['READY', 'TRAVELING']);
export const TODAY_HISTORICAL_LIFECYCLES = Object.freeze(['COMPLETED']);
export const TODAY_UNAVAILABLE_LIFECYCLES = Object.freeze(['DRAFT', 'CANCELLED', 'ARCHIVED']);

export const ACTIVITY_STATUS_LABELS = Object.freeze({
  PLANNED: '已規劃',
  CONFIRMED: '已確認',
  IN_PROGRESS: '進行中',
  COMPLETED: '已完成',
  CANCELLED: '已取消',
});

/** Today activity-execution chrome (zh-TW; matches existing Journey Today copy style). */
export const TODAY_ACTIVITY_EXECUTION_COPY = Object.freeze({
  confirm: '確認',
  start: '開始',
  complete: '完成',
  cancel: '取消',
  cancelling: '取消中…',
  cancelDialogTitle: '取消這個活動？',
  cancelDialogBody: '取消後無法從此畫面還原。狀態會與伺服器同步。',
  cancelReasonLabel: '取消原因（選填）',
  confirmCancel: '確認取消',
  dismissCancel: '返回',
  working: '處理中…',
  successConfirm: '活動已確認。',
  successStart: '活動已開始。',
  successComplete: '活動已完成。',
  successCancel: '活動已取消。',
  conflict: '行程已在其他地方更新，請依最新狀態再試。',
  validation: '無法執行此操作，請檢查後再試。',
  forbidden: '你沒有權限操作這個活動。',
  notFound: '找不到這個活動或旅程。',
  network: '網路異常，行程狀態未變更。請稍後再試。',
  missingVersion: '行程版本尚未就緒，請稍後再試。',
  refreshFailed: '操作可能已送出，但無法重新載入最新行程。請重新整理後再試，勿使用舊版本繼續操作。',
  generic: '無法執行活動操作。',
});

export function activityExecutionActionLabel(action) {
  const key = String(action || '');
  if (key === 'CONFIRM') return TODAY_ACTIVITY_EXECUTION_COPY.confirm;
  if (key === 'START') return TODAY_ACTIVITY_EXECUTION_COPY.start;
  if (key === 'COMPLETE') return TODAY_ACTIVITY_EXECUTION_COPY.complete;
  if (key === 'CANCEL') return TODAY_ACTIVITY_EXECUTION_COPY.cancel;
  return key;
}

export function activityStatusLabel(status) {
  const key = String(status || '');
  return ACTIVITY_STATUS_LABELS[key] || key || '—';
}

export function calendarDatePart(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
  }
  if (Array.isArray(value) && value.length >= 3) {
    const y = String(value[0]).padStart(4, '0');
    const m = String(value[1]).padStart(2, '0');
    const d = String(value[2]).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return '';
}

function sortDays(days) {
  return [...(Array.isArray(days) ? days : [])]
    .filter((day) => day?.journeyDayId && day?.date)
    .sort((a, b) => {
      const dateCmp = String(a.date).localeCompare(String(b.date));
      if (dateCmp !== 0) return dateCmp;
      return (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0);
    });
}

export function selectJourneyDay({
  lifecycleStatus,
  calendarDate,
  startDate,
  days,
  historicalDayId,
} = {}) {
  const status = String(lifecycleStatus || '');
  const ordered = sortDays(days);
  const matching = ordered.filter((day) => day.date === calendarDate);

  if (status === 'DRAFT') {
    return { mode: 'UNAVAILABLE', selectedDay: null, matchingCount: matching.length, labeledToday: false };
  }
  if (status === 'CANCELLED' || status === 'ARCHIVED') {
    return { mode: 'TERMINAL', selectedDay: null, matchingCount: matching.length, labeledToday: false };
  }
  if (!TODAY_LIVE_LIFECYCLES.includes(status) && status !== 'COMPLETED') {
    return { mode: 'UNKNOWN', selectedDay: null, matchingCount: matching.length, labeledToday: false };
  }

  if (status === 'COMPLETED') {
    const requested = historicalDayId == null ? null : String(historicalDayId);
    const selected = (requested && ordered.find((day) => String(day.journeyDayId) === requested))
      || ordered[ordered.length - 1]
      || null;
    return {
      mode: 'HISTORICAL',
      selectedDay: selected,
      matchingCount: matching.length,
      labeledToday: false,
    };
  }

  if (status === 'READY' && startDate && calendarDate && calendarDate < startDate) {
    return {
      mode: 'PREVIEW',
      selectedDay: ordered[0] || null,
      matchingCount: matching.length,
      labeledToday: false,
    };
  }

  if (matching.length === 0) {
    return {
      mode: 'NO_DAY',
      selectedDay: null,
      matchingCount: 0,
      labeledToday: status === 'TRAVELING' || status === 'READY',
    };
  }

  return {
    mode: 'LIVE',
    selectedDay: matching[0],
    matchingCount: matching.length,
    labeledToday: true,
  };
}

export function activitiesForSelectedDay(schedule, journeyDayId) {
  const dayId = journeyDayId == null ? '' : String(journeyDayId);
  if (!dayId) return [];
  const days = Array.isArray(schedule?.days) ? schedule.days : [];
  const day = days.find((item) => String(item.journeyDayId) === dayId);
  const activities = Array.isArray(day?.activities) ? [...day.activities] : [];
  return activities.sort((a, b) => {
    const order = (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0);
    if (order !== 0) return order;
    return String(a.activityId || '').localeCompare(String(b.activityId || ''));
  });
}

function activityStartMinutes(activity) {
  return clockMinutes(activity?.plannedStartTime);
}

export function remainingTodayActivities(activities) {
  return (Array.isArray(activities) ? activities : []).filter((activity) => {
    const status = String(activity?.status || '');
    return status !== 'CANCELLED' && status !== 'COMPLETED';
  });
}

/**
 * Frontend heuristic only. Not GPS / live tracking.
 * Timed remaining activities with plannedStartTime >= current clock win;
 * otherwise first remaining by display order (covers untimed).
 */
export function selectNextActivity(activities, clockTime) {
  const remaining = remainingTodayActivities(activities);
  if (!remaining.length) return null;
  const nowMins = clockMinutes(clockTime);
  if (nowMins != null) {
    const upcomingTimed = remaining
      .filter((activity) => {
        const start = activityStartMinutes(activity);
        return start != null && start >= nowMins;
      })
      .sort((a, b) => {
        const timeCmp = activityStartMinutes(a) - activityStartMinutes(b);
        if (timeCmp !== 0) return timeCmp;
        return (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0);
      });
    if (upcomingTimed.length) return upcomingTimed[0];
  }
  return remaining[0];
}

export function staysForCalendarDate(stays, calendarDate) {
  const date = calendarDatePart(calendarDate);
  if (!date) return [];
  return (Array.isArray(stays) ? stays : []).filter((stay) => {
    const checkIn = calendarDatePart(stay?.checkInAt);
    const checkOut = calendarDatePart(stay?.checkOutAt);
    if (!checkIn || !checkOut) return false;
    return checkIn <= date && date < checkOut;
  });
}

export function transportsForCalendarDate(transports, calendarDate) {
  const date = calendarDatePart(calendarDate);
  if (!date) return [];
  const matched = (Array.isArray(transports) ? transports : []).filter((item) => {
    const departure = calendarDatePart(item?.departureAt);
    const arrival = calendarDatePart(item?.arrivalAt);
    return departure === date || arrival === date;
  });
  return matched.sort((a, b) => {
    const aKey = String(a.departureAt || a.arrivalAt || '');
    const bKey = String(b.departureAt || b.arrivalAt || '');
    if (aKey && bKey && aKey !== bKey) return aKey.localeCompare(bKey);
    return String(a.transportId || '').localeCompare(String(b.transportId || ''));
  });
}

export function checklistItemsForToday(items, calendarDate) {
  const date = calendarDatePart(calendarDate);
  return (Array.isArray(items) ? items : [])
    .filter((item) => !item?.completed && String(item?.status || 'OPEN') !== 'COMPLETED')
    .map((item) => {
      const dueDate = calendarDatePart(item?.dueAt);
      let urgency = 'NONE';
      if (dueDate && date) {
        if (dueDate < date) urgency = 'OVERDUE';
        else if (dueDate === date) urgency = 'DUE_TODAY';
      }
      return { ...item, urgency };
    });
}

export function decisionsNeedingAttention(decisions) {
  return (Array.isArray(decisions) ? decisions : []).filter((item) => (
    String(item?.decisionStatus) === 'OPEN'
    && Boolean(item?.actorEligible)
    && !item?.actorHasVoted
  ));
}

export function journalComposeHref(journeyId, { journeyDayId, activityId } = {}) {
  const id = snowflakeId(journeyId);
  const params = new URLSearchParams({ compose: '1' });
  if (journeyDayId) params.set('journeyDayId', String(journeyDayId));
  if (activityId) params.set('activityId', String(activityId));
  return `/journeys/${id}/journal?${params.toString()}`;
}

/**
 * Today lifecycle continuity CTAs only: Start Trip / Complete Trip.
 * Does not expose ready / cancel / archive transitions (those stay on Overview).
 * Reuses journeyLifecycleModel capability rules (OWNER/COORDINATOR start; OWNER-only complete).
 */
export function todayJourneyLifecycleAction(journey) {
  if (!journey?.requesterRole) return null;
  const caps = lifecycleActionCapabilities(journey);
  if (caps.canStart) return 'START';
  if (caps.canComplete) return 'COMPLETE';
  return null;
}

export function todayModeCopy(mode, lifecycleStatus) {
  if (mode === 'UNAVAILABLE') {
    return {
      eyebrow: 'Today',
      title: '今天尚未開始',
      hint: '旅程仍在規劃中。請先完成行程安排，再從總覽標記準備完成。',
    };
  }
  if (mode === 'TERMINAL') {
    return {
      eyebrow: 'Today',
      title: lifecycleStatus === 'ARCHIVED' ? '已封存' : '旅程已取消',
      hint: '這個旅程已結束，沒有進行中的今日助手。',
    };
  }
  if (mode === 'PREVIEW') {
    return {
      eyebrow: '準備出發',
      title: '出發前預覽',
      hint: '這是第一天的行程預覽，還不是「今天」。',
    };
  }
  if (mode === 'HISTORICAL') {
    return {
      eyebrow: '回顧',
      title: '旅程已完成',
      hint: '這是歷史行程檢視，不是即時的今天。',
    };
  }
  if (mode === 'NO_DAY') {
    return {
      eyebrow: 'Today',
      title: '今天沒有對應的行程日',
      hint: '目前日期沒有 Journey Day。Today 不會自動建立新的一天。',
    };
  }
  if (mode === 'UNKNOWN') {
    return {
      eyebrow: 'Today',
      title: '無法判斷旅程狀態',
      hint: '未知的旅程狀態，因此不提供今日操作。',
    };
  }
  return {
    eyebrow: lifecycleStatus === 'READY' ? '準備出發' : '旅程進行中',
    title: '今天',
    hint: '',
  };
}
